import React, { Component } from 'react';
import { COLOR, SIZE } from '../../../../utils/ColorPickerValue';
import { Doctor, Tutor, Patient, Student } from '../../../../utils/common/NamingTerms';
import store from '../../../../sessionStorage';
import { connect } from 'react-redux';
import { onWhiteBoardDraw, startChatSocketConnect } from '../../../action/SocketActions';
import { showNotification } from '../../../action/NotificationAction';
import LanguageContext, { LanguageProvider } from '../../../../utils/common/LanguageContext';
import {
    getWhiteboardDataForSession,
    pruneWhiteboardSessionsExcept,
    saveWhiteboardDataForSession,
    setActiveWhiteboardSession,
    onWhiteboardDataExpired,
} from '../../../../utils/WhiteboardSessionStorage';

const withLanguage = (WrappedComponent) => {
    return class extends Component {
        render() {
            return (
                <LanguageContext.Consumer>
                    {({ t }) => <WrappedComponent {...this.props} t={t} />}
                </LanguageContext.Consumer>
            );
        }
    };
};

class ScreenShotEditModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            messages: [],
            textMessage: '',
            chatImage: '',
            receiverId: '',
            visitId: this.props.visitId,
            fileUrls: [],
            loading: false,
            educatorFileUrls: [],
        };
        this.canvasRef = React.createRef();
        this.ctx = null;
        this.isDrawing = false;
        this.prevPos = { x: 0, y: 0 };
        this.undoStack = [];
        this.redoStack = [];
        this.colors = ['#000000', '#FF0000', '#00FF00', '#0000FF'];
        this.currentColorIndex = 0;
        this.isErasing = false;
        this.colorId = Object.keys(COLOR)[0];
        this.sizeId = 0;
        this.fileInputRef = React.createRef();
        this.resizeRaf = null;
        // In-memory snapshot — single source of truth for drawing content
        this._currentSnapshot = null;
        this._resizeObserver = null;
        // Track canvas size at the time snapshot was saved
        this._snapshotWidth = 0;
        this._snapshotHeight = 0;
        this._unsubscribeExpiration = null; // Store unsubscribe function for cleanup
        this._snapshotBitmap = null;
    }

    getEffectiveSessionId = (preferredVisitId) => {
        if (
            preferredVisitId !== null &&
            preferredVisitId !== undefined &&
            preferredVisitId !== ''
        ) {
            return preferredVisitId;
        }
        if (
            this.props.visitId !== null &&
            this.props.visitId !== undefined &&
            this.props.visitId !== ''
        ) {
            return this.props.visitId;
        }
        if (
            this.state.visitId !== null &&
            this.state.visitId !== undefined &&
            this.state.visitId !== ''
        ) {
            return this.state.visitId;
        }
        if (typeof window !== 'undefined' && window.sessionStorage) {
            return window.sessionStorage.getItem('WHITEBOARD_SESSION_ID') || '';
        }
        return '';
    };

    // ─── Lifecycle ────────────────────────────────────────────────────────────

    componentDidMount() {
        const visitId = this.getEffectiveSessionId(this.props.visitId);
        this.setState({ visitId });
        setActiveWhiteboardSession(visitId);
        pruneWhiteboardSessionsExcept(visitId);

        const commonSession = {
            ...store.get('userSession'),
            ...store.get('insAdminSession'),
        };
        if (commonSession.userType) {
            this.props.startChatSocketConnect(commonSession.id, Doctor, visitId, this.props);
        } else {
            this.props.startChatSocketConnect(commonSession.id, Patient, visitId, this.props);
        }

        // Load saved snapshot into memory before anything renders
        this._currentSnapshot = getWhiteboardDataForSession(visitId) || null;
        this._snapshotWidth = 0;
        this._snapshotHeight = 0;

        // Init canvas 2d context only — size set by ResizeObserver below
        this.ctx = this.canvasRef.current.getContext('2d');

        // ResizeObserver fires AFTER the canvas element has truly finished
        // resizing (fullscreen toggle, panel open/close, window resize, etc.)
        this._resizeObserver = new ResizeObserver(() => {
            this._onCanvasSizeChanged();
        });
        this._resizeObserver.observe(this.canvasRef.current);

        window.addEventListener('beforeunload', this.handleBeforeUnload);

        // Listen for whiteboard data expiration
        this._unsubscribeExpiration = onWhiteboardDataExpired((expiredSessionId) => {
            const currentVisitId = this.getEffectiveSessionId(this.state.visitId);
            if (currentVisitId === expiredSessionId) {
                // console.log(`Whiteboard data expired for current session ${expiredSessionId}, clearing canvas`);
                // Clear the canvas
                if (this.ctx && this.canvasRef.current) {
                    this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
                    this.undoStack = [];
                    this.redoStack = [];
                    this._currentSnapshot = null;
                    if (this._snapshotBitmap) {
                        this._snapshotBitmap.close();
                        this._snapshotBitmap = null;
                    }
                }
                // Show notification to user
                // if (this.props.showNotification) {
                //     this.props.showNotification(
                //         'Whiteboard data has expired and been cleared',
                //         'info'
                //     );
                // }
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.visitId !== this.props.visitId) {
            const visitId = this.getEffectiveSessionId(this.props.visitId);
            if (!visitId) {
                return;
            }
            setActiveWhiteboardSession(visitId);
            pruneWhiteboardSessionsExcept(visitId);
            this._currentSnapshot = getWhiteboardDataForSession(visitId) || null;
            this._snapshotWidth = 0;
            this._snapshotHeight = 0;
            // ✅ Free old session bitmap
            if (this._snapshotBitmap) {
                this._snapshotBitmap.close();
                this._snapshotBitmap = null;
            }
            this.setState({ visitId }, () => {
                this._applySnapshot();
            });
        }
        if (prevProps.whiteBoardDrawList !== this.props.whiteBoardDrawList) {
            this.drawReceivedMessages(this.props.whiteBoardDrawList);
        }
    }

    //componentWillUnmount
    componentWillUnmount() {
        if (this.resizeRaf) {
            window.cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = null;
        }
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        // ✅ Free GPU memory held by ImageBitmap
        if (this._snapshotBitmap) {
            this._snapshotBitmap.close();
            this._snapshotBitmap = null;
        }
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        
        // Unsubscribe from expiration listener
        if (this._unsubscribeExpiration) {
            this._unsubscribeExpiration();
            this._unsubscribeExpiration = null;
        }
        
        this.stopPolling();
    }

    // ─── Canvas Sizing ────────────────────────────────────────────────────────

    _onCanvasSizeChanged = () => {
        const canvas = this.canvasRef.current;
        if (!canvas) return;

        const newWidth = canvas.offsetWidth;
        const newHeight = canvas.offsetHeight;

        if (!newWidth || !newHeight) return;

        // Guard: skip if size truly hasn't changed to avoid infinite loop
        // (setting canvas.width itself triggers ResizeObserver again)
        if (canvas.width === newWidth && canvas.height === newHeight) {
            return;
        }

        // Disconnect observer before changing dimensions to prevent
        // the dimension change from re-triggering this callback
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Canvas resets all context styles on dimension change — reapply
        if (this.ctx) {
            this.ctx.strokeStyle = this.getCurrentColor();
            this.ctx.lineWidth = SIZE[this.sizeId] * 2;
        }

        // Repaint snapshot at new canvas size
        this._applySnapshot();

        // Reconnect observer after DOM settles via rAF
        if (this.resizeRaf) {
            window.cancelAnimationFrame(this.resizeRaf);
        }
        this.resizeRaf = window.requestAnimationFrame(() => {
            this.resizeRaf = null;
            if (this._resizeObserver && this.canvasRef.current) {
                this._resizeObserver.observe(this.canvasRef.current);
            }
        });
    };

    // Save snapshot + record the canvas dimensions it was drawn at
    // Save snapshot + cache ImageBitmap for instant synchronous repaints
    _saveSnapshot = () => {
        const canvas = this.canvasRef.current;
        if (!canvas) return;
        this._currentSnapshot = canvas.toDataURL();
        this._snapshotWidth = canvas.width;
        this._snapshotHeight = canvas.height;

        // Pre-decode into ImageBitmap — paint is then instant with no async delay
        createImageBitmap(canvas).then((bitmap) => {
            // Revoke previous bitmap to free memory
            if (this._snapshotBitmap) {
                this._snapshotBitmap.close();
            }
            this._snapshotBitmap = bitmap;
        });
    };

    // Paint snapshot onto canvas at its ORIGINAL pixel size — never stretched.
    // Canvas may be larger (fullscreen) but drawing stays same physical size.
    // Paint snapshot INSTANTLY using cached ImageBitmap — zero decode delay
    _applySnapshot = () => {
        const canvas = this.canvasRef.current;
        if (!canvas || !this.ctx) return;

        this.ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!this._currentSnapshot) return;

        // ✅ If bitmap is ready — paint synchronously, zero delay
        if (this._snapshotBitmap) {
            const drawW = this._snapshotWidth || this._snapshotBitmap.width;
            const drawH = this._snapshotHeight || this._snapshotBitmap.height;
            this.ctx.strokeStyle = this.getCurrentColor();
            this.ctx.lineWidth = SIZE[this.sizeId] * 2;
            this.ctx.drawImage(this._snapshotBitmap, 0, 0, drawW, drawH, 0, 0, drawW, drawH);
            return;
        }

        // Fallback: bitmap not ready yet (first load from storage)
        // Use Image() async path only in this case
        const snapshotAtCallTime = this._currentSnapshot;
        const snapW = this._snapshotWidth;
        const snapH = this._snapshotHeight;

        const img = new Image();
        img.onload = () => {
            if (!this.ctx || !this.canvasRef.current) return;
            if (this._currentSnapshot !== snapshotAtCallTime) return;

            const c = this.canvasRef.current;
            const drawW = snapW || img.naturalWidth;
            const drawH = snapH || img.naturalHeight;

            this.ctx.strokeStyle = this.getCurrentColor();
            this.ctx.lineWidth = SIZE[this.sizeId] * 2;
            this.ctx.clearRect(0, 0, c.width, c.height);
            this.ctx.drawImage(img, 0, 0, drawW, drawH, 0, 0, drawW, drawH);

            // Cache as bitmap now so next repaint is instant
            createImageBitmap(img).then((bitmap) => {
                if (this._snapshotBitmap) {
                    this._snapshotBitmap.close();
                }
                this._snapshotBitmap = bitmap;
            });
        };
        img.src = snapshotAtCallTime;
    };

    // ─── Drawing ──────────────────────────────────────────────────────────────

    startDrawing = (e) => {
        const { sessionType } = this.props;
        const userSession = store.get('userSession');
        const role = userSession && userSession.role;

        if (sessionType === 'OneToOne') {
            if (role !== Tutor && role !== Student) return;
        } else {
            if (role !== Tutor) return;
        }
        this.isDrawing = true;
        this.prevPos = this.getMousePos(e);
        this.ctx.lineWidth = SIZE[this.sizeId] * 2;
    };

    draw = (e) => {
        const { sessionType } = this.props;
        const userSession = store.get('userSession');
        const role = userSession && userSession.role;

        if (!this.isDrawing) return;
        if (sessionType === 'OneToOne') {
            if (role !== Tutor && role !== Student) return;
        } else {
            if (role !== Tutor) return;
        }

        const currentPos = this.getMousePos(e);

        if (this.isErasing) {
            this.ctx.clearRect(
                this.prevPos.x,
                this.prevPos.y,
                currentPos.x - this.prevPos.x,
                currentPos.y - this.prevPos.y
            );
        } else {
            this.ctx.beginPath();
            this.ctx.moveTo(this.prevPos.x, this.prevPos.y);
            this.ctx.lineTo(currentPos.x, currentPos.y);
            this.ctx.stroke();
        }
        this.prevPos = currentPos;
    };

    stopDrawing = () => {
        const { sessionType } = this.props;
        const userSession = store.get('userSession') || store.get('insAdminSession');
        if (!userSession || !userSession.role) return;
        if (sessionType === 'OneToOne') {
            if (userSession.role !== Tutor && userSession.role !== Student) return;
        } else {
            if (userSession.role !== Tutor) return;
        }
        if (this.isDrawing) {
            this.saveToUndoStack();
            this.isDrawing = false;
            this._saveSnapshot();
            this.saveToLocalStorage();
            this.saveWhiteBoardData();
        }
    };

    // scaleX/scaleY always 1.0 because ResizeObserver keeps
    // canvas.width === offsetWidth and canvas.height === offsetHeight
    getMousePos = (e) => {
        const canvas = this.canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    };

    // ─── Canvas Operations ────────────────────────────────────────────────────

    clearCanvas = () => {
        const { sessionType } = this.props;
        const userSession = store.get('userSession');
        const role = userSession && userSession.role;
        if (sessionType === 'OneToOne') {
            if (role !== Tutor && role !== Student) return;
        } else {
            if (role !== Tutor) return;
        }
        this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
        this.undoStack = [];
        this.redoStack = [];
        this._currentSnapshot = null;
        this._snapshotWidth = 0;
        this._snapshotHeight = 0;
        // ✅ Free bitmap on clear
        if (this._snapshotBitmap) {
            this._snapshotBitmap.close();
            this._snapshotBitmap = null;
        }
        this.saveToLocalStorage();
        this.saveWhiteBoardData();
    };

    // saveToUndoStack
    saveToUndoStack = () => {
        const imgData = this.ctx.getImageData(
            0,
            0,
            this.canvasRef.current.width,
            this.canvasRef.current.height
        );
        this.undoStack.push(imgData);
        if (this.redoStack.length) {
            this.redoStack = [];
        }
    };

    // undo
    undo = () => {
        const { sessionType } = this.props;
        const userSession = store.get('userSession');
        const role = userSession && userSession.role;

        if (sessionType === 'OneToOne') {
            if (role !== Tutor && role !== Student) return;
        } else {
            if (role !== Tutor) return;
        }
        if (this.undoStack.length > 1) {
            this.redoStack.push(this.undoStack.pop());
            this.ctx.putImageData(this.undoStack[this.undoStack.length - 1], 0, 0);
            this._saveSnapshot();
            this.saveToLocalStorage();
            this.saveWhiteBoardData();
        } else {
            this.clearCanvas();
        }
    };

    //redo
    redo = () => {
        const { sessionType } = this.props;
        const userSession = store.get('userSession');
        const role = userSession && userSession.role;

        if (sessionType === 'OneToOne') {
            if (role !== Tutor && role !== Student) return;
        } else {
            if (role !== Tutor) return;
        }
        if (this.redoStack.length) {
            this.ctx.putImageData(this.redoStack[this.redoStack.length - 1], 0, 0);
            this.undoStack.push(this.redoStack.pop());
            this._saveSnapshot();
            this.saveToLocalStorage();
            this.saveWhiteBoardData();
        }
    };

    toggleEraser = () => {
        const { sessionType } = this.props;
        const userSession = store.get('userSession');
        const role = userSession && userSession.role;

        if (sessionType === 'OneToOne') {
            if (role !== Tutor && role !== Student) return;
        } else {
            if (role !== Tutor) return;
        }
        this.isErasing = true;
    };

    getCurrentColor = () => {
        return this.colors[this.currentColorIndex];
    };

    // ─── Storage ──────────────────────────────────────────────────────────────

    saveWhiteBoardData = () => {
        const visitId = this.getEffectiveSessionId();
        const userSession = store.get('userSession');
        const canvasData = this._currentSnapshot || this.canvasRef.current.toDataURL();
        const messagePayload = {
            textMessage: canvasData,
            visitId,
            senderUserId: userSession.id,
        };
        this.props.onWhiteBoardDraw(messagePayload, (message) => {
            this.setState((prevState) => ({
                messages: [...prevState.messages, message],
            }));
        });
    };

    drawReceivedMessages = (drawList) => {
        if (!drawList || drawList.length === 0) return;
        clearTimeout(this.drawTimeout);
        this.drawTimeout = setTimeout(() => {
            const canvas = this.canvasRef.current;
            if (!canvas) return;

            const offscreen = document.createElement('canvas');
            offscreen.width = canvas.width;
            offscreen.height = canvas.height;
            const offscreenCtx = offscreen.getContext('2d');
            let loaded = 0;

            drawList.forEach((base64Image) => {
                const img = new Image();
                img.src = base64Image;
                img.onload = () => {
                    offscreenCtx.drawImage(img, 0, 0, offscreen.width, offscreen.height);
                    loaded++;
                    if (loaded === drawList.length) {
                        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
                        this.ctx.drawImage(offscreen, 0, 0, canvas.width, canvas.height);
                        this._saveSnapshot();
                        this.saveToLocalStorage();
                    }
                };
            });
        }, 100);
    };

    saveToLocalStorage = () => {
        const visitId = this.getEffectiveSessionId();
        const data = this._currentSnapshot || this.canvasRef.current?.toDataURL() || '';
        saveWhiteboardDataForSession(visitId, data);
    };

    handleBeforeUnload = () => {
        const userSession = store.get('userSession');
        if (!userSession || !userSession.role) return;
        if (userSession.role === Tutor || userSession.role === Student) {
            const visitId = this.getEffectiveSessionId();
            saveWhiteboardDataForSession(
                visitId,
                this._currentSnapshot || this.canvasRef.current?.toDataURL() || ''
            );
        }
    };

    stopPolling = () => {
        clearInterval(this.pollingInterval);
    };

    handleOpenFileDialog = () => {
        this.fileInputRef.current.click();
    };

    handleFileChange = (event) => {
        const files = event.target.files;
        if (files.length > 0) {
            this.uploadFiles(files);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    render() {
        return (
            <div style={{ display: 'flex', width: '100%', height: '100vh' }}>
                <div
                    style={{
                        display: 'flex',
                        backgroundColor: 'white',
                        flex: 1,
                        width: '100%',
                        height: '100%',
                        minWidth: 0,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '1rem',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: '0.1',
                                marginTop: '5rem',
                            }}
                        >
                            <button
                                style={{ cursor: 'pointer' }}
                                className="mb-3"
                                onClick={this.undo}
                            >
                                {this.props.t('undo')}
                            </button>
                            <button
                                style={{ cursor: 'pointer' }}
                                className="mb-3"
                                onClick={this.redo}
                            >
                                {this.props.t('redo')}
                            </button>
                            <button
                                style={{ cursor: 'pointer' }}
                                className="mb-3"
                                onClick={this.clearCanvas}
                            >
                                {this.props.t('clear')}
                            </button>
                            <button
                                style={{ cursor: 'pointer' }}
                                className="mb-3"
                                onClick={this.toggleEraser}
                            >
                                {this.props.t('eraser')}
                            </button>
                            <input
                                type="file"
                                ref={this.fileInputRef}
                                className="hidden"
                                accept=".png, .jpeg, .jpg"
                                onChange={this.handleFileChange}
                                multiple
                            />
                        </div>
                        <div>
                            {this.colors.map((c) => (
                                <div
                                    key={c}
                                    style={{
                                        display: 'inline-block',
                                        width: 20,
                                        height: 20,
                                        background: c,
                                        border: '1px solid black',
                                        margin: 5,
                                        cursor: 'pointer',
                                        borderRadius: '5px',
                                    }}
                                    onClick={() => {
                                        this.ctx.strokeStyle = c;
                                        this.isErasing = false;
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                    <canvas
                        ref={this.canvasRef}
                        onMouseDown={this.startDrawing}
                        onMouseMove={this.draw}
                        onMouseUp={this.stopDrawing}
                        onMouseLeave={this.stopDrawing}
                        style={{
                            border: '1px solid black',
                            background: '#fff',
                            flex: 1,
                            minWidth: 0,
                            height: '100%',
                            display: 'block',
                        }}
                    />
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        whiteBoardDrawList: state.WhiteBoardReducerList.whiteBoardDrawList,
        sessionType: state.WhiteBoardReducerList.sessionType,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onWhiteBoardDraw: (input, callback) => {
            dispatch(onWhiteBoardDraw(input, callback));
        },
        startChatSocketConnect: (userId, userType, visitId, props) => {
            dispatch(startChatSocketConnect(userId, userType, visitId, props));
        },
        showNotification: (message, type) => {
            dispatch(showNotification(message, type));
        },
    };
};

const ConnectedScreenShotEditModal = withLanguage(
    connect(mapStateToProps, mapDispatchToProps)(ScreenShotEditModal)
);

export default (props) => (
    <LanguageProvider>
        <ConnectedScreenShotEditModal {...props} />
    </LanguageProvider>
);
